<template>
  <div class="integration-container">
    <!-- Page Header -->
    <div class="page-header">
      <h1>Integration</h1>
      <div class="header-actions">
        <button @click="showAddPlatformForm = true" class="btn btn-primary">
          <i class="icon-plus"></i> Add Platform
        </button>
        <button @click="generateApiKey" class="btn btn-secondary">
          <i class="icon-key"></i> Generate API Key
        </button>
      </div>
    </div>

    <div v-if="error" class="error-message">
      {{ error }}
    </div>
    <div v-if="success" class="success-message">
      {{ success }}
    </div>

    <!-- Main Content Area -->
    <div class="main-content">
      <!-- Left Panel: Integration Categories -->
      <div class="left-panel">
        <div class="panel-header">
          <h3>Integration Types</h3>
        </div>
        
        <div class="categories-list">
          <div 
            v-for="category in categories" 
            :key="category.id" 
            class="category-item"
            :class="{ 'active': selectedCategory === category.id }"
            @click="selectCategory(category.id)"
          >
            <div class="category-icon">
              <i :class="category.icon"></i>
            </div>
            <div class="category-info">
              <h4>{{ category.name }}</h4>
              <p>{{ category.description }}</p>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Right Panel: Integration Content -->
      <div class="right-panel">
        <!-- Chat Platforms Management -->
        <div v-if="selectedCategory === 'chatPlatforms'" class="settings-panel">
          <div class="panel-header">
            <h3>Chat Platforms</h3>
            <button @click="showAddPlatformForm = true" class="btn btn-primary">
              <i class="icon-key"></i> Add Platform
            </button>
          </div>
          
          <!-- Add/Edit Platform Form -->
          <div v-if="showAddPlatformForm || showEditPlatformForm" class="platform-form">
            <h4>{{ showEditPlatformForm ? 'Edit' : 'Add' }} Chat Platform</h4>
            <form @submit.prevent="showEditPlatformForm ? updateChatPlatform() : addChatPlatformKey()">
              <div class="form-row">
                <div class="form-group">
                  <label>Platform Type *</label>
                  <select v-model="platformForm.type" required :disabled="showEditPlatformForm">
                    <option value="">Select Platform</option>
                    <option value="telegram">Telegram</option>
                    <option value="discord">Discord</option>
                    <option value="slack">Slack</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="line">Line</option>
                    <option value="webchat">WebChat</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Platform Name *</label>
                <input
                  v-model="platformForm.name"
                  type="text"
                  placeholder="Enter platform name (e.g., 'Customer Support Bot')"
                  required
                >
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>Instance ID</label>
                  <input
                    v-model="platformForm.instanceId"
                    type="text"
                    placeholder="Unique instance identifier (auto-generated if empty)"
                    :readonly="showEditPlatformForm"
                    :class="{ 'readonly-field': showEditPlatformForm }"
                  >
                  <small class="form-help">{{ showEditPlatformForm ? 'Instance ID cannot be changed after creation' : 'Leave empty to auto-generate a unique instance ID' }}</small>
                </div>
              </div>
              <div class="form-group">
                <label>{{ platformForm.type === 'line' ? 'Channel Access Token' : 'API Key/Token' }} {{ showEditPlatformForm ? '(leave empty to keep current)' : '*' }}</label>
                <input
                  v-model="platformForm.apiKey"
                  type="password"
                  :placeholder="showEditPlatformForm ? 'Enter new token or leave empty' : (platformForm.type === 'line' ? 'Enter Line channel access token' : 'Enter API key or token')"
                  :required="!showEditPlatformForm"
                >
                <small v-if="showEditPlatformForm" class="form-help">Current token is hidden for security. Only enter a new token if you want to change it.</small>
                <small v-if="!showEditPlatformForm && platformForm.type === 'line'" class="form-help">Get this from your Line Messaging API channel settings</small>
              </div>
              
              <!-- Line-specific Channel Secret field -->
              <div class="form-group" v-if="platformForm.type === 'line'">
                <label>Channel Secret {{ showEditPlatformForm ? '(leave empty to keep current)' : '*' }}</label>
                <input
                  v-model="platformForm.channelSecret"
                  type="password"
                  :placeholder="showEditPlatformForm ? 'Enter new channel secret or leave empty' : 'Enter Line channel secret'"
                  :required="!showEditPlatformForm && platformForm.type === 'line'"
                >
                <small class="form-help">The channel secret from your Line Messaging API channel</small>
              </div>
              <div class="form-group" v-if="['telegram', 'discord'].includes(platformForm.type)">
                <label>{{ platformForm.type === 'telegram' ? 'Bot Username' : 'Bot Name' }}</label>
                <input
                  v-model="platformForm.botUsername"
                  type="text"
                  :placeholder="platformForm.type === 'telegram' ? '@your_bot_username (e.g., @supportbot)' : 'Your bot name or ID'"
                >
                <small class="form-help">{{ platformForm.type === 'telegram' ? 'The username of your Telegram bot (include @)' : 'The name or ID of your Discord bot' }}</small>
              </div>
              <div class="form-group">
                <label>Webhook URL</label>
                <input
                  v-model="platformForm.webhookUrl"
                  type="url"
                  :placeholder="getWebhookPlaceholder()"
                  readonly
                >
                <small class="form-help">Auto-generated webhook URL for this platform and organization</small>
              </div>

              <div class="form-group">
                <label>Allowed Agents</label>
                <div v-if="agents.length === 0" class="empty-agents">
                  <small class="form-help text-warning">No agents available. Create agents first to assign them to this platform.</small>
                </div>
                <div v-else class="agents-selector">
                  <div class="agent-selection-header">
                    <small class="form-help">Select which agents can interact through this platform:</small>
                  </div>
                  <div class="agent-checkboxes">
                    <label v-for="agent in agents" :key="agent.id" class="checkbox-label agent-checkbox">
                      <input 
                        type="checkbox" 
                        :value="agent.id" 
                        v-model="platformForm.allowedAgents"
                      >
                      <div class="agent-info">
                        <span class="agent-name">{{ agent.name || agent.id }}</span>
                        <span v-if="agent.description" class="agent-description">{{ agent.description }}</span>
                      </div>
                    </label>
                  </div>
                  <div class="selection-summary">
                    <small class="form-help selection-count">
                      <strong>{{ platformForm.allowedAgents.length }}</strong> of {{ agents.length }} agents selected
                      <span v-if="platformForm.allowedAgents.length === 0" class="text-warning"> (No agents selected - platform will be inactive)</span>
                    </small>
                  </div>
                </div>
              </div>
              <div class="checkbox-group">
                <label class="checkbox-label">
                  <input type="checkbox" v-model="platformForm.enabled">
                  <span>Enable this platform</span>
                </label>
              </div>
              <div class="form-actions">
                <button type="button" @click="cancelPlatformForm" class="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" :disabled="platformFormLoading">
                  {{ platformFormLoading ? 'Processing...' : (showEditPlatformForm ? 'Update' : 'Add') }} Platform
                </button>
              </div>
            </form>
          </div>
          
          <div class="settings-content">
            <div class="form-section">
              <div class="platforms-header">
                <h4>Configured Platforms</h4>
                <div class="platforms-count" v-if="chatPlatforms.length > 0">
                  {{ chatPlatforms.length }} platform{{ chatPlatforms.length !== 1 ? 's' : '' }} configured
                </div>
              </div>
              <div v-if="chatPlatforms.length === 0" class="empty-state">
                <i class="icon-chat"></i>
                <h4>No Chat Platforms</h4>
                <p>No chat platforms configured yet. Click "Add Platform" to get started.</p>
              </div>
              <div class="platforms-list">
                <div v-for="platform in chatPlatforms" :key="platform.id || platform.platform" class="platform-card">
                <div class="platform-header">
                  <div class="platform-info">
                    <h5>{{ platform.config?.name || platform.name || platform.platform }}</h5>
                    <span class="platform-type">{{ platform.platform }}</span>
                    <span class="platform-status" :class="{ 'active': platform.enabled }">
                      {{ platform.enabled ? 'Active' : 'Inactive' }}
                    </span>
                  </div>
                  <div class="platform-actions">
                    <button @click="testPlatformConnection(platform)" class="btn btn-outline" title="Test Connection">
                      <i class="icon-test"></i> Test
                    </button>
                    <button @click="editChatPlatformKey(platform)" class="btn btn-outline" title="Edit">
                      <i class="icon-edit"></i> Edit
                    </button>
                    <button @click="removeChatPlatformKey(platform)" class="btn btn-danger" title="Remove">
                      <i class="icon-trash"></i> Remove
                    </button>
                  </div>
                </div>
                <div class="platform-details">
                  <p class="key-preview">Token: ***{{ (platform.config?.token || '').slice(-4) }}***</p>
                  <p class="key-created">Added on {{ formatDate(platform.created_at) }}</p>
                  <p v-if="platform.config?.webhook_url" class="webhook-url">Webhook: {{ platform.config.webhook_url }}</p>
                  <p v-if="platform.allowed_agents && platform.allowed_agents.length > 0" class="allowed-agents">
                    Allowed Agents: {{ platform.allowed_agents.length }} configured
                  </p>
                </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- API & Integrations -->
        <div v-else-if="selectedCategory === 'api'" class="settings-panel">
          <div class="panel-header">
            <h3>API & Integrations</h3>
            <button @click="generateApiKey" class="btn btn-primary">
              <i class="icon-key"></i> Generate API Key
            </button>
          </div>
          
          <div class="settings-content">
            <div class="form-section">
              <h4>API Keys</h4>
              <div v-if="apiKeys.length === 0" class="empty-state">
                <i class="icon-key"></i>
                <h4>No API Keys</h4>
                <p>Generate your first API key to start integrating with our API.</p>
              </div>
              <div v-else class="api-keys-list">
                <div v-for="key in apiKeys" :key="key.id" class="api-key-item">
                  <div class="key-info">
                    <h5>{{ key.name }}</h5>
                    <p class="key-preview">{{ key.key_preview }}</p>
                    <p class="key-created">Created {{ formatDate(key.created_at) }}</p>
                  </div>
                  <div class="key-actions">
                    <button @click="copyApiKey(key)" class="btn btn-outline">
                      <i class="icon-copy"></i> Copy
                    </button>
                    <button @click="revokeApiKey(key.id)" class="btn btn-danger">
                      <i class="icon-trash"></i> Revoke
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="form-section">
              <h4>Webhook Settings</h4>
              <div class="form-group">
                <label>Webhook URL</label>
                <input
                  v-model="webhookSettings.url"
                  type="url"
                  placeholder="https://your-domain.com/webhook"
                >
              </div>
              <div class="checkbox-group">
                <label class="checkbox-label">
                  <input type="checkbox" v-model="webhookSettings.enabled">
                  <span>Enable webhooks</span>
                </label>
              </div>
              <div class="form-actions">
                <button @click="saveWebhookSettings" class="btn btn-primary">
                  Save Webhook Settings
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Welcome Panel -->
        <div v-else class="welcome-panel">
          <div class="welcome-content">
            <i class="icon-integration"></i>
            <h3>Integration Management</h3>
            <p>Manage your API keys, webhooks, and chat platform integrations. Select a category from the left to get started.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.integration-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
  overflow: hidden;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  background: white;
  border-bottom: 1px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.page-header h1 {
  margin: 0;
  color: #2c3e50;
  font-size: 2rem;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.error-message,
.success-message {
  padding: 0.75rem 2rem;
  margin: 0;
  font-weight: 500;
}

.error-message {
  background: #f8d7da;
  color: #721c24;
  border-bottom: 1px solid #f1aeb5;
}

.success-message {
  background: #d1e7dd;
  color: #0f5132;
  border-bottom: 1px solid #a3cfbb;
}

.main-content {
  flex: 1;
  display: flex;
  min-height: 0;
}

.left-panel {
  width: 320px;
  background: white;
  border-right: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
}

.panel-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
}

.panel-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.25rem;
  font-weight: 600;
}

.categories-list {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
}

.category-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  margin-bottom: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.category-item:hover {
  background: #f8f9fa;
  border-color: #6f42c1;
}

.category-item.active {
  background: #f8f6ff;
  border-color: #6f42c1;
  box-shadow: 0 2px 8px rgba(111, 66, 193, 0.15);
}

.category-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: linear-gradient(135deg, #6f42c1, #5a2d91);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.category-info {
  flex: 1;
  min-width: 0;
}

.category-info h4 {
  margin: 0 0 0.25rem;
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
}

.category-info p {
  margin: 0;
  font-size: 0.85rem;
  color: #6c757d;
  line-height: 1.4;
}

.right-panel {
  flex: 1;
  background: white;
  overflow-y: auto;
}

.settings-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.settings-panel .panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e9ecef;
  background: white;
}

.settings-content {
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
}

.form-section {
  margin-bottom: 3rem;
}

.form-section h4 {
  margin: 0 0 1.5rem;
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 600;
}

.platform-form {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
}

.platform-form h4 {
  margin: 0 0 1.5rem;
  color: #2c3e50;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: border-color 0.2s ease;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #6f42c1;
  box-shadow: 0 0 0 3px rgba(111, 66, 193, 0.1);
}

.form-help {
  display: block;
  font-size: 0.85rem;
  color: #6c757d;
  margin-top: 0.25rem;
  line-height: 1.4;
}

.text-warning {
  color: #f0ad4e;
}

.readonly-field {
  background-color: #f8f9fa;
  color: #6c757d;
}

.checkbox-group {
  margin-bottom: 1.5rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.95rem;
  color: #2c3e50;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: auto;
  margin: 0;
}

.agents-selector {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 1rem;
  background: white;
}

.agent-checkboxes {
  max-height: 200px;
  overflow-y: auto;
  margin: 1rem 0;
}

.agent-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 0.5rem;
  transition: background 0.2s ease;
}

.agent-checkbox:hover {
  background: #f8f9fa;
}

.agent-info {
  flex: 1;
}

.agent-name {
  display: block;
  font-weight: 500;
  color: #2c3e50;
}

.agent-description {
  display: block;
  font-size: 0.85rem;
  color: #6c757d;
  margin-top: 0.25rem;
}

.form-actions {
  display: flex;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #6f42c1;
  color: white;
}

.btn-primary:hover {
  background: #5a2d91;
}

.btn-secondary {
  background: #f8f9fa;
  color: #6c757d;
  border: 1px solid #dee2e6;
}

.btn-secondary:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.btn-outline {
  background: white;
  color: #6f42c1;
  border: 1px solid #6f42c1;
}

.btn-outline:hover {
  background: #6f42c1;
  color: white;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover {
  background: #c82333;
}

.platforms-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.platforms-count {
  color: #6c757d;
  font-size: 0.9rem;
}

.empty-state {
  text-align: center;
  color: #6c757d;
  padding: 3rem 2rem;
}

.empty-state i {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.3;
  color: #6f42c1;
}

.empty-state h4 {
  margin: 0 0 0.5rem;
  color: #495057;
}

.empty-state p {
  margin: 0;
  font-size: 0.9rem;
}

.platforms-list {
  display: grid;
  gap: 1rem;
}

.platform-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1.5rem;
  background: white;
  transition: all 0.2s ease;
}

.platform-card:hover {
  border-color: #6f42c1;
  box-shadow: 0 2px 8px rgba(111, 66, 193, 0.1);
}

.platform-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.platform-info {
  flex: 1;
}

.platform-info h5 {
  margin: 0 0 0.5rem;
  color: #2c3e50;
  font-size: 1.1rem;
}

.platform-type {
  background: #f3e8ff;
  color: #6f42c1;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  margin-right: 0.5rem;
}

.platform-status {
  background: #ffebee;
  color: #c62828;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.platform-status.active {
  background: #e8f5e8;
  color: #2e7d32;
}

.platform-actions {
  display: flex;
  gap: 0.5rem;
}

.platform-details p {
  margin: 0 0 0.5rem;
  font-size: 0.85rem;
  color: #6c757d;
}

.api-keys-list {
  display: grid;
  gap: 1rem;
}

.api-key-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: white;
}

.key-info h5 {
  margin: 0 0 0.25rem;
  color: #2c3e50;
}

.key-info p {
  margin: 0;
  font-size: 0.85rem;
  color: #6c757d;
}

.key-actions {
  display: flex;
  gap: 0.5rem;
}

.welcome-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.welcome-content {
  text-align: center;
  color: #6c757d;
  max-width: 500px;
}

.welcome-content i {
  font-size: 5rem;
  margin-bottom: 1.5rem;
  opacity: 0.3;
  color: #6f42c1;
}

.welcome-content h3 {
  margin: 0 0 1rem;
  color: #2c3e50;
  font-size: 1.5rem;
}

.welcome-content p {
  margin: 0;
  line-height: 1.6;
}

/* Icons */
.icon-plus:before { content: '➕'; }
.icon-key:before { content: '🔑'; }
.icon-api:before { content: '🔌'; }
.icon-chat:before { content: '💬'; }
.icon-integration:before { content: '🔗'; }
.icon-test:before { content: '🧪'; }
.icon-edit:before { content: '✏️'; }
.icon-trash:before { content: '🗑️'; }
.icon-copy:before { content: '📋'; }

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    flex-direction: column;
  }
  
  .left-panel {
    width: 100%;
    max-height: 300px;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }

  .header-actions {
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'IntegrationView',
  setup() {
    const store = useStore()
    
    // Reactive state
    const error = ref('')
    const success = ref('')
    const selectedCategory = ref('chatPlatforms')
    const showAddPlatformForm = ref(false)
    const showEditPlatformForm = ref(false)
    const platformFormLoading = ref(false)

    // Integration categories
    const categories = [
      {
        id: 'chatPlatforms',
        name: 'Chat Platforms',
        description: 'Manage chat platform integrations',
        icon: 'icon-chat'
      },
      {
        id: 'api',
        name: 'API & Webhooks',
        description: 'API keys and webhooks',
        icon: 'icon-api'
      }
    ]

    // Forms
    const platformForm = reactive({
      type: '',
      name: '',
      instanceId: '',
      apiKey: '',
      channelSecret: '',
      botUsername: '',
      webhookUrl: '',
      enabled: true,
      allowedAgents: [],
      currentPlatform: null
    })

    const webhookSettings = reactive({
      url: '',
      enabled: false
    })

    // Computed properties
    const chatPlatforms = computed(() => {
      const rawPlatforms = store.state.messagingPlatforms?.items || []
      return Array.isArray(rawPlatforms) ? rawPlatforms.filter(platform => platform && typeof platform === 'object') : []
    })

    const agents = computed(() => {
      const rawAgents = store.state.agents?.items || []
      return Array.isArray(rawAgents) ? rawAgents.filter(agent => agent && typeof agent === 'object' && agent.id) : []
    })

    const apiKeys = ref([])

    // Methods
    const selectCategory = (categoryId) => {
      selectedCategory.value = categoryId
      showAddPlatformForm.value = false
      showEditPlatformForm.value = false
    }

    const getWebhookPlaceholder = () => {
      if (!platformForm.type) {
        return 'Select a platform type first'
      }
      const instanceId = platformForm.instanceId || 'auto-generated-instance-id'
      return `https://api.yourapp.com/webhook/${platformForm.type}/${instanceId}`
    }

    const addChatPlatformKey = async () => {
      try {
        platformFormLoading.value = true
        
        const payload = {
          platform: platformForm.type,
          name: platformForm.name,
          allowed_agents: platformForm.allowedAgents,
          config: {
            token: platformForm.apiKey,
            bot_username: platformForm.botUsername,
            webhook_url: platformForm.webhookUrl,
            enabled: platformForm.enabled
          }
        }
        
        if (platformForm.type === 'line' && platformForm.channelSecret) {
          payload.config.channel_secret = platformForm.channelSecret
        }
        
        await store.dispatch('messagingPlatforms/create', payload)
        
        success.value = 'Chat platform added successfully!'
        setTimeout(() => success.value = '', 3000)
        
        cancelPlatformForm()
        await fetchChatPlatforms()
      } catch (err) {
        console.error('Error adding chat platform:', err)
        error.value = 'Failed to add chat platform'
        setTimeout(() => error.value = '', 3000)
      } finally {
        platformFormLoading.value = false
      }
    }

    const editChatPlatformKey = (platform) => {
      // Reset and populate form for editing
      platformForm.type = platform.platform || platform.type || ''
      platformForm.name = platform.config?.name || platform.name || ''
      platformForm.instanceId = platform.instance_id || platform.config?.instance_id || ''
      platformForm.apiKey = ''
      platformForm.channelSecret = ''
      platformForm.botUsername = platform.config?.bot_username || ''
      platformForm.webhookUrl = platform.config?.webhook_url || ''
      platformForm.enabled = platform.config?.enabled !== undefined ? platform.config.enabled : platform.enabled !== undefined ? platform.enabled : true
      platformForm.allowedAgents = platform.allowed_agents || []
      platformForm.currentPlatform = platform
      
      showEditPlatformForm.value = true
    }

    const updateChatPlatform = async () => {
      try {
        platformFormLoading.value = true
        
        const updates = {
          name: platformForm.name,
          allowed_agents: platformForm.allowedAgents,
          config: {
            bot_username: platformForm.botUsername,
            webhook_url: platformForm.webhookUrl,
            enabled: platformForm.enabled
          }
        }
        
        if (platformForm.apiKey) {
          updates.config.token = platformForm.apiKey
        }
        
        if (platformForm.type === 'line' && platformForm.channelSecret) {
          updates.config.channel_secret = platformForm.channelSecret
        }
        
        await store.dispatch('messagingPlatforms/update', {
          id: platformForm.currentPlatform.id,
          updates
        })
        
        success.value = 'Platform updated successfully!'
        setTimeout(() => success.value = '', 3000)
        
        cancelPlatformForm()
        await fetchChatPlatforms()
      } catch (err) {
        console.error('Error updating platform:', err)
        error.value = 'Failed to update platform'
        setTimeout(() => error.value = '', 3000)
      } finally {
        platformFormLoading.value = false
      }
    }

    const removeChatPlatformKey = async (platform) => {
      if (!confirm('Are you sure you want to remove this platform?')) return
      
      try {
        await store.dispatch('messagingPlatforms/delete', platform.id)
        success.value = 'Platform removed successfully!'
        setTimeout(() => success.value = '', 3000)
        await fetchChatPlatforms()
      } catch (err) {
        console.error('Error removing platform:', err)
        error.value = 'Failed to remove platform'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const testPlatformConnection = async (platform) => {
      try {
        await store.dispatch('messagingPlatforms/testConnection', { platform: platform.platform })
        success.value = `Connection to ${platform.config?.name || platform.name} successful!`
        setTimeout(() => success.value = '', 3000)
      } catch (err) {
        console.error('Error testing platform connection:', err)
        error.value = `Failed to connect to ${platform.config?.name || platform.name}`
        setTimeout(() => error.value = '', 3000)
      }
    }

    const cancelPlatformForm = () => {
      platformForm.type = ''
      platformForm.name = ''
      platformForm.instanceId = ''
      platformForm.apiKey = ''
      platformForm.channelSecret = ''
      platformForm.botUsername = ''
      platformForm.webhookUrl = ''
      platformForm.enabled = true
      platformForm.allowedAgents = []
      platformForm.currentPlatform = null
      
      showAddPlatformForm.value = false
      showEditPlatformForm.value = false
      platformFormLoading.value = false
    }

    const generateApiKey = async () => {
      try {
        const name = prompt('Enter a name for this API key:')
        if (!name) return
        
        const newKey = await store.dispatch('auth/generateApiKey', { name })
        apiKeys.value.push(newKey)
        
        success.value = 'API key generated successfully!'
        setTimeout(() => success.value = '', 3000)
      } catch (err) {
        console.error('Error generating API key:', err)
        error.value = 'Failed to generate API key'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const copyApiKey = (key) => {
      navigator.clipboard.writeText(key.full_key)
      success.value = 'API key copied to clipboard!'
      setTimeout(() => success.value = '', 3000)
    }

    const revokeApiKey = async (keyId) => {
      if (!confirm('Are you sure you want to revoke this API key?')) return
      
      try {
        await store.dispatch('auth/revokeApiKey', keyId)
        apiKeys.value = apiKeys.value.filter(key => key.id !== keyId)
        
        success.value = 'API key revoked successfully!'
        setTimeout(() => success.value = '', 3000)
      } catch (err) {
        console.error('Error revoking API key:', err)
        error.value = 'Failed to revoke API key'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const saveWebhookSettings = async () => {
      try {
        await store.dispatch('updateWebhookSettings', webhookSettings)
        success.value = 'Webhook settings saved successfully!'
        setTimeout(() => success.value = '', 3000)
      } catch (err) {
        console.error('Error saving webhook settings:', err)
        error.value = 'Failed to save webhook settings'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const fetchChatPlatforms = async () => {
      try {
        await store.dispatch('messagingPlatforms/fetchAll')
      } catch (err) {
        console.error('Error fetching chat platforms:', err)
        error.value = 'Failed to load chat platforms'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const fetchAgents = async () => {
      try {
        await store.dispatch('agents/fetchAll')
      } catch (err) {
        console.error('Error fetching agents:', err)
      }
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      try {
        return new Date(dateString).toLocaleDateString()
      } catch (e) {
        return 'Invalid Date'
      }
    }

    // Lifecycle
    onMounted(async () => {
      try {
        await Promise.all([
          fetchChatPlatforms(),
          fetchAgents()
        ])
      } catch (error) {
        console.error('Error loading integration data:', error)
        error.value = 'Failed to load integration data'
        setTimeout(() => error.value = '', 3000)
      }
    })

    return {
      // State
      error,
      success,
      selectedCategory,
      showAddPlatformForm,
      showEditPlatformForm,
      platformFormLoading,
      categories,
      platformForm,
      webhookSettings,
      apiKeys,
      
      // Computed
      chatPlatforms,
      agents,
      
      // Methods
      selectCategory,
      getWebhookPlaceholder,
      addChatPlatformKey,
      editChatPlatformKey,
      updateChatPlatform,
      removeChatPlatformKey,
      testPlatformConnection,
      cancelPlatformForm,
      generateApiKey,
      copyApiKey,
      revokeApiKey,
      saveWebhookSettings,
      formatDate
    }
  }
}
</script>